<template>
  <Layout>
    <template v-slot:top_left>
      <Link :href="exit_action_path">
        <q-btn
          flat
          icon="las la-arrow-left"
          color="primary"
        />
      </Link>
      <AvatarImage
        :img-url="$page.props.shared.wg.icon_url"
        :label="$page.props.shared.wg.name"
        avatar-size="24px"
        img-size="20px"
      />
      <span class="q-ml-sm text-primary text-bold">{{ $page.props.shared.wg.name }}</span>
    </template>

    <template v-slot:header>
      <span class="text-primary text-bold">{{ $page.props.shared.workspace.name }}</span>
    </template>

    <template v-slot:default>
      <q-page class="column full-height q-pa-none text-center">
        <span class="text-h4 q-pt-xl text-bold q-mb-sm">{{ i18n.page_title }}</span>
        <p
          class="text-h6"
          style="font-weight: 300"
        >
          {{ i18n.page_subtitle }}
        </p>

        <div class="row wrap justify-center items-start content-start q-mt-sm q-col-gutter-lg">
          <!-- Monthly/Annual toggle -->
          <div
            v-if="billing_intervals.length > 1"
            class="col-12 justify-center items-center align-center content-center"
          >
            <q-btn-toggle
              v-model="billing_interval_selected"
              unelevated
              color="drawer-background"
              toggle-color="primary"
              text-color="dark-text"
              class="text-caption"
              no-caps
              :options="[
                { label: i18n.billing_interval_month, value: 'monthly' },
                { label: i18n.billing_interval_year, value: 'yearly' },
              ]"
            />

            <p class="text-positive q-mt-md">
              {{ i18n.billing_interval_tip }}
            </p>
          </div>
        </div>

        <template v-if="offers.length == 0">
          <div class="row justify-center items-start content-start q-mt-sm q-col-gutter-lg">
            <div class="col-12 text-center">
              <img
                src="/illustrations/no_offers.svg"
                :alt="t('subscription.subscriptionChoice.noOffers')"
                class="q-img-responsive"
                width="450"
              >
            </div>

            <div class="col-12 text-center">
              <h5 class="text-bold">
                {{ t('subscription.subscriptionChoice.noOffers') }}
              </h5>

              <p class="text-subtitle1 q-pt-sm q-mb-none">
                {{ t('subscription.subscriptionChoice.noOffersForOrganization') }}
              </p>

              <p class="text-bold">
                {{ t('subscription.subscriptionChoice.contactSupport') }}
              </p>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="centered-container-large row justify-center items-start content-start q-col-gutter-md q-pa-xl">
            <template
              v-for="offer in offers"
              :key="offer.id"
            >
              <div
                v-if="offer.billing_periods.includes(billing_interval_selected)"
                class="col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 text-center self-stretch q-mt-xl"
              >
                <q-card
                  class="q-pa-none fit bg-drawer-background"
                  :round="false"
                  :class="{
                    'custom-offer': offer.highlighted,
                    'disabled': offer.disabled,
                  }"
                >
                  <!-- HIGHLIGHTED OFFER -->
                  <q-card-section
                    v-if="offer.highlighted"
                    class="bg-pastel-green q-py-sm absolute-top"
                    style="top: -38px; border-radius: 8px 8px 0 0"
                  >
                    <span class="text-default">{{ offer.highlighted_title }}</span>
                  </q-card-section>

                  <!-- OFFER TITLE -->
                  <q-card-section class="q-pt-lg card-background">
                    <span
                      class="text-h5 q-mb-none"
                      style="font-weight: 300"
                    >{{ offer.title }}</span>
                  <!-- <p>{{ offer.description }}</p> -->
                  </q-card-section>

                  <!-- OFFER PRICE -->
                  <q-card-section class="card-background q-pt-none">
                    <q-item
                      class="q-mx-auto card-background q-px-none"
                      style="width: fit-content"
                    >
                      <!-- Business -->
                      <q-item-section
                        v-if="exclude_taxes"
                        side
                        class="offer_price"
                      >
                        {{ billing_interval_selected == 'yearly' ? offer.price_per_year : offer.price_per_month }}
                      </q-item-section>

                      <!-- Individual -->
                      <q-item-section
                        v-else
                        side
                        class="offer_price"
                      >
                        {{ offer.price_per_year_ttc }}
                      </q-item-section>

                      <q-item-section class="text-left">
                        <!-- Business -->
                        <q-item-label
                          v-if="exclude_taxes"
                          lines="1"
                          class="text-bold"
                        >
                          {{ i18n.offer_price_per_year }}
                        </q-item-label>

                        <!-- Individual -->
                        <q-item-label
                          v-else
                          lines="1"
                          class="text-bold"
                        >
                          {{ i18n.offer_price_per_year_tax }}
                        </q-item-label>


                        <q-item-label
                          caption
                          lines="1"
                          class="text-muted"
                        >
                          {{ billing_interval_selected == 'yearly' ? '' : i18n.offer_commitment }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-card-section>

                  <!-- OFFER BUTTON -->
                  <q-card-section class="q-pa-md card-background q-pb-md">
                    <q-btn
                      :disabled="offer.disabled"
                      class="full-width"
                      color="primary"
                      :label="i18n.offer_action_subscribe"
                      unelevated
                      no-caps
                      @click="checkout(offer)"
                    >
                      <q-tooltip
                        v-if="offer.disabled"
                      >
                        {{ i18n.offer_disabled }}
                      </q-tooltip>
                    </q-btn>
                  </q-card-section>

                  <!-- OFFER FEATURES -->
                  <q-separator class="" />

                  <q-card-section class="">
                    <!-- show only enabled features -->
                    <q-list
                      flat
                      class="text-left"
                      dense
                    >
                      <q-item
                        v-for="(feature, f_index) in offer.features"
                        :key="`${offer.id}-feature-${f_index}`"
                        dense
                        style="padding: 0"
                      >
                        <q-item-section
                          avatar
                          class="q-pr-none"
                          style="min-width: 32px"
                        >
                          <q-icon
                            color="positive"
                            size="18px"
                            name="fa fa-circle-check"
                          />
                        </q-item-section>
                        <q-item-section>
                          <q-item-label> <span v-html="feature" /></q-item-label>
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </q-card-section>
                </q-card>
              </div>
            </template>
            <div class="col-12 q-pt-xl">
              <p
                class="text-muted"
                v-html="i18n.custom_offer"
              />
            </div>
          </div>

          <div
            class="row justify-center bg-pastel-purple items-start content-start q-my-xl q-py-xl q-col-gutter-md"
          >
            <div class="col-12 q-pt-none text-center self-stretch q-pa-xl text-dark">
              <span class="text-h3">{{ i18n.enterprise_solutions_title }}</span>
              <p class=" q-mt-lg">
                {{ i18n.enterprise_solutions_description_part1 }}
                <br>
                {{ i18n.enterprise_solutions_description_part2 }}
              </p>
              <q-btn
                class="q-mt-md"
                color="black"
                :label="i18n.contact_us"
                unelevated
                no-caps
                href="https://comptacrypto.com/contact"
                target="_blank"
              />
            </div>
          </div>
        </template>
      </q-page>
    </template>
  </Layout>
</template>
<script>
import AvatarImage from '@/shared/avatar_image.vue';
import Layout from '@/layouts/wide.vue';
import { Link } from '@inertiajs/vue3';

export default {
  components: {
    AvatarImage,
    Layout,
    Link,
  },
  props: {
    i18n: {
      type: Object,
      required: true,
    },
    checkout_success_url: {
      type: String,
      required: true,
    },
    checkout_canceled_url: {
      type: String,
      required: true,
    },
    exit_action_path: {
      type: String,
      required: true,
    },
    checkout_status: {
      type: String,
      required: false,
    },
    checkout_success_redirect_path: {
      type: String,
      required: true,
    },
    offers: {
      type: Array,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    billing_intervals: {
      type: Array,
      required: true,
    },
    exclude_taxes: {
      type: Boolean,
      required: true,
    },
    subscription: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      billing_interval_selected: this.billing_intervals[0], // 'yearly', 'monthly'
    };
  },
  computed: {
  },
  watch: {
  },
  mounted() {
    if (this.checkout_status) {
      this.checkout_return();
    }
  },
  methods: {
    t(str, other) {
      console.log(str);
      console.log(other);
      return 'missing: ' + str;
    },
    history_back() {
      window.history.back();
    },
    checkout: function(offer) {
      let params = {
        workspace_id: this.$page.props.shared.workspace.id,
        offer_id: offer.id,
        historical_data_enabled: false,
        interval: this.billing_interval_selected,
        plan: "plan"
      };

      params.success_url = this.checkout_success_url;
      params.cancel_url = this.checkout_canceled_url;

      let url = "/subscription/checkout";
      this.$rails.post(url, params)
        .then((response) => {
          if (!response.ok) {
            throw new Error(this.i18n.error);
          }
          return response.json();
        })
        .then((json) => {
          window.location.href = json.checkout_url;
        })
        .catch((error) => {
          console.log(error);
          this.$q.notify({
            message: error,
            multiline: true,
            type: "negative",
          });
        });
    },
    checkout_return: function() {
      if (this.checkout_status == 'success') {
        this.$q.notify({
          message: this.i18n.checkout_success,
          type: "positive",
        });
        setTimeout(() => {
          this.$inertia.get(this.checkout_success_redirect_path);
        }, 1000);
      }
    },
  },
};
</script>
<style scoped>
/* remove top rounded border for custom */
.custom-offer {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}

.q-item {
  user-select: text;
}
</style>
